import { Button } from '@dev-spendesk/grapes';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { ModalMFAContainer } from 'common/components/ModalMFA';
import { useTranslation } from 'common/hooks/useTranslation';
import { useCompany } from 'modules/app/hooks/useCompany';
import { HubTaskWrapper } from 'modules/onboarding/components/HubTaskWrapper';
import { ReadyToSpendModalSlideshow } from 'modules/onboarding/setup-hub/components/ReadyToSpendModalSlideshow';
import { getContentByTaskType } from 'modules/onboarding/setup-hub/components/ReadyToSpendPopover/components/ReadyToSpendTaskItem/utils';
import { useCheckPhoneNumberAndRedirect } from 'modules/onboarding/setup-hub/hooks/useCheckPhoneNumberAndRedirect';
import { useCompleteOnboardingUserTaskMutation } from 'modules/onboarding/setup-hub/hooks/useCompleteOnboardingUserTaskMutation';
import {
  type UserTaskType,
  type UserTask,
} from 'modules/onboarding/setup-hub/types/userTask';
import { routeFor, routes } from 'src/core/constants/routes';
import { LocalStorageKey } from 'src/core/constants/storage';
import { setLocalItem } from 'src/core/utils/storage';

type Props = {
  task: UserTask;
  sectionId: string;
};
export const UserTaskItem = ({ task, sectionId }: Props) => {
  const { t } = useTranslation('global');
  const content = getContentByTaskType(task.type);
  const company = useCompany();
  const history = useHistory();

  const [isSlideshowOpen, setIsSlideshowOpen] = useState(false);
  const {
    isMfaModalOpen,
    trigger,
    handleMfaModalClose,
    handleFactorActivated,
  } = useCheckPhoneNumberAndRedirect();

  const [completeOnboardingUserTaskMutation] =
    useCompleteOnboardingUserTaskMutation();

  if (!content) {
    return null;
  }

  const openRequestsPage = (type: UserTaskType) => {
    const path = routeFor(routes.REQUESTS.path, {
      company: company.id,
      type: 'all',
      id: 'new',
      newRequestType: 'virtual',
    });
    const params =
      type === 'subscription'
        ? `?card-type=subscription`
        : '?card-type=single-purchase';
    history.push(`${path}${params}`, {
      productTourToDisplay:
        task.type === 'single_use' ? 'single_purchase' : 'subscription',
    });
  };

  const openTask = async () => {
    setLocalItem(LocalStorageKey.LastOnboardingSectionOpened, sectionId);
    switch (task.type) {
      case 'download_app':
      case 'learn_to_approve':
      case 'upload_receipt':
        setIsSlideshowOpen(true);
        break;
      case 'expense_claim': {
        const path = routeFor(routes.REQUESTS.path, {
          company: company.id,
          type: 'all',
          id: 'new',
          newRequestType: 'reimbursement',
        });
        history.push(path, { productTourToDisplay: 'reimbursement' });
        break;
      }
      case 'single_use':
      case 'subscription': {
        trigger(() => openRequestsPage(task.type));
        break;
      }
      default:
        break;
    }
  };

  const skipTask = async () => {
    setLocalItem(
      LocalStorageKey.LastOnboardingSectionOpened,
      'ready_to_spend',
      true,
    );
    if (task.status !== 'completed') {
      await completeOnboardingUserTaskMutation({
        endpointParams: { taskType: task.type },
      });
    }
  };

  return (
    <>
      <HubTaskWrapper
        title={t(content.title)}
        description={t(content.description)}
        task={task}
        showTagStatus
        actions={
          task.status !== 'completed'
            ? [
                <Button
                  key="start"
                  variant="secondaryNeutral"
                  onClick={openTask}
                  text={t(content.cta)}
                />,
                <Button
                  key="skip"
                  iconName="cross"
                  variant="tertiaryNeutral"
                  onClick={skipTask}
                  text={t('misc.skip')}
                />,
              ]
            : []
        }
      />
      {isSlideshowOpen && (
        <ReadyToSpendModalSlideshow
          onCloseClicked={() => setIsSlideshowOpen(false)}
          type={task.type}
        />
      )}
      {isMfaModalOpen && (
        <ModalMFAContainer
          isOpen={isMfaModalOpen}
          isReset
          onModalClose={handleMfaModalClose}
          onFactorActivated={handleFactorActivated}
        />
      )}
    </>
  );
};
